import { useState } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { states } from "js/placeholderData/states";
import FormInputError from "components/General/FormInputError";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";

export default function FACStepThree({
  formStep,
  setFormStep,
  setFACFormData,
}) {
  const uuid = uuidv4();
  const MAXIMUMWORDCOUNT = 300;
  const [currentWordCount, setCurrentWordCount] = useState(0);
  const { t } = useTranslation();

  const {
    register: FACStepThreeRegister,
    handleSubmit: FACStepThreeHandleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      mailingFax: "",
    },
  });

  const watchDeliveryMethod = watch("claimsPacketDeliveryMethod");

  const onSubmit = (data) => {
    setFormStep(4);
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });
  };

  return (
    <>
      {formStep === 3 && (
        <div>
          <p className="text-sm">{t("AgentFACStepThree-headingText")}</p>

          <form onSubmit={FACStepThreeHandleSubmit(onSubmit)}>
            <hr />

            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div className="grid gap-4 grid-cols-1 md:gap-y-0">
                <fieldset>
                  <legend className="font-semibold">
                    {t("AgentFACStepThree-claimsPacketDeliveryHeading")}
                  </legend>
                  <div className="items-center text-[10px] leading-4 text-ui-red font-semibold">
                    {t("AgentFACStepThree-claimsPacketDeliverySubHeading")}
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-claimsPacketDeliveryMethod-email`}
                        type="radio"
                        className="mr-1"
                        value="email"
                        {...FACStepThreeRegister("claimsPacketDeliveryMethod", {
                          required: t(
                            "AgentFACStepThree-claimsPacketDeliveryMethod-emailRequiredMessage"
                          ),
                        })}
                      />
                      {t(
                        "AgentFACStepThree-claimsPacketDeliveryMethod-emailLabel"
                      )}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-claimsPacketDeliveryMethod-fax`}
                        type="radio"
                        className="mr-1"
                        value="fax"
                        {...FACStepThreeRegister("claimsPacketDeliveryMethod", {
                          required: t(
                            "AgentFACStepThree-claimsPacketDeliveryMethod-emailRequiredMessage"
                          ),
                        })}
                      />
                      {t(
                        "AgentFACStepThree-claimsPacketDeliveryMethod-faxLabel"
                      )}
                    </label>
                  </div>
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-semibold">
                      <input
                        id={`${uuid}-claimsPacketDeliveryMethod-fedex`}
                        type="radio"
                        className="mr-1"
                        value="fedex"
                        {...FACStepThreeRegister("claimsPacketDeliveryMethod", {
                          required: t(
                            "AgentFACStepThree-claimsPacketDeliveryMethod-emailRequiredMessage"
                          ),
                        })}
                      />
                      {t(
                        "AgentFACStepThree-claimsPacketDeliveryMethod-fadexLabel"
                      )}
                    </label>
                  </div>
                  <FormInputError
                    id={`${uuid}-claimsPacketDeliveryMethod-error`}
                    errorMessage={errors.claimsPacketDeliveryMethod?.message}
                  />
                </fieldset>
              </div>

              {watchDeliveryMethod === "email" && (
                <div className="grid gap-4 grid-cols-1">
                  <div className="md:w-4/5">
                    <label
                      htmlFor={`${uuid}-email-mailing`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepThree-mailing-emailLabel")}
                    </label>
                    <input
                      id={`${uuid}-email-mailing`}
                      type="text"
                      placeholder={t(
                        "AgentFACStepThree-mailing-emailPlaceholder"
                      )}
                      {...FACStepThreeRegister(`mailingEmail`, {
                        required: t(
                          "AgentFACStepThree-mailing-emailRequiredMessage"
                        ),
                        pattern: {
                          value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                          message: t(
                            "AgentFACStepThree-mailing-emailPatternMessage"
                          ),
                        },
                      })}
                    />
                    <FormInputError
                      id={`${uuid}-email-mailing-error`}
                      errorMessage={errors?.mailingEmail?.message}
                    />
                  </div>
                </div>
              )}

              {watchDeliveryMethod === "fax" && (
                <div className="grid gap-4 grid-cols-1">
                  <div className="md:w-4/5">
                    <label id={`${uuid}-faxNumber`} className="block mb-1">
                      {t("AgentFACStepThree-mailing-faxLabel")}
                    </label>
                    <MaskedPhoneNumberInput
                      name="mailingFax"
                      describedby={`${uuid}-faxNumber`}
                      register={FACStepThreeRegister}
                      setValue={setValue}
                    />
                    <FormInputError
                      errorMessage={errors?.mailingFax?.message}
                    />
                  </div>
                </div>
              )}

              {watchDeliveryMethod === "fedex" && (
                <div className="grid gap-4 grid-cols-1 lg:gap-6">
                  <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
                    <div>
                      <label
                        htmlFor={`${uuid}-fn-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-firstNameLabel")}
                      </label>
                      <input
                        id={`${uuid}-fn-mailing`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepThree-mailing-firstNamePlaceholder"
                        )}
                        {...FACStepThreeRegister(`mailingFirstName`, {
                          required: t(
                            "AgentFACStepThree-mailing-firstNameRequiredMessage"
                          ),
                        })}
                      />
                      <FormInputError
                        id={`${uuid}-fn-mailing-error`}
                        errorMessage={errors?.mailingFirstName?.message}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`${uuid}-ln-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-lastName")}
                      </label>
                      <input
                        id={`${uuid}-ln-mailing`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepThree-mailing-lastNamePlaceholder"
                        )}
                        {...FACStepThreeRegister(`mailingLastName`, {
                          required: t(
                            "AgentFACStepThree-mailing-lastNameRequiredMessage"
                          ),
                        })}
                      />
                      <FormInputError
                        id={`${uuid}-ln-mailing-error`}
                        errorMessage={errors?.mailingLastName?.message}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`${uuid}-cn-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-companyName")}
                      </label>
                      <input
                        id={`${uuid}-cn-mailing`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepThree-mailing-companyNamePlaceholder"
                        )}
                        {...FACStepThreeRegister(`mailingCompanyName`)}
                      />
                      <FormInputError
                        id={`${uuid}-cn-mailing-error`}
                        errorMessage={errors?.mailingCompanyName?.message}
                      />
                    </div>
                  </div>

                  <div className="md:w-4/5">
                    <label
                      htmlFor={`${uuid}-ma-mailing`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepThree-mailing-addressLabel")}
                    </label>
                    <input
                      id={`${uuid}-ma-mailing`}
                      type="text"
                      placeholder={t(
                        "AgentFACStepThree-mailing-addressPlaceholder"
                      )}
                      {...FACStepThreeRegister(`mailingAddress`, {
                        required: t(
                          "AgentFACStepThree-mailing-addressRequiredMessage"
                        ),
                      })}
                    />
                    <FormInputError
                      id={`${uuid}-ma-mailing-error`}
                      errorMessage={errors?.mailingAddress?.message}
                    />
                  </div>

                  <div className="md:w-4/5">
                    <label
                      htmlFor={`${uuid}-ma2-mailing`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepThree-mailing-addressLabel2")}
                    </label>
                    <input
                      id={`${uuid}-ma2-mailing`}
                      type="text"
                      placeholder={t(
                        "AgentFACStepThree-mailing-addressPlaceholder2"
                      )}
                      {...FACStepThreeRegister(`mailingAddress2`)}
                    />
                    <FormInputError
                      id={`${uuid}-ma2-mailing-error`}
                      errorMessage={errors?.mailingAddress2?.message}
                    />
                  </div>

                  <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
                    <div>
                      <label
                        htmlFor={`${uuid}-city-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-cityLabel")}
                      </label>
                      <input
                        id={`${uuid}-city-mailing`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepThree-mailing-cityPlaceholder"
                        )}
                        {...FACStepThreeRegister("mailingCity", {
                          required: t(
                            "AgentFACStepThree-mailing-cityRequiredText"
                          ),
                        })}
                      />
                      <FormInputError
                        id={`${uuid}-city-mailing-error`}
                        errorMessage={errors.mailingCity?.message}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`${uuid}-state-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-stateLabel")}
                      </label>
                      <select
                        id={`${uuid}-state-mailing`}
                        placeholder="Mailing State"
                        {...FACStepThreeRegister("mailingState", {
                          required: t(
                            "AgentFACStepThree-mailing-statePlaceholder"
                          ),
                        })}
                      >
                        <option value="">
                          {t("AgentFACStepThree-mailing-stateOptionLabel")}
                        </option>
                        {states.map((state, idx) => (
                          <option
                            value={state.abbreviation}
                            key={`state - ${state.name} - ${idx}`}
                          >
                            {state.name}
                          </option>
                        ))}
                      </select>

                      <FormInputError
                        id={`${uuid}-state-mailing-error`}
                        errorMessage={errors.mailingState?.message}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor={`${uuid}-zip-mailing`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepThree-mailing-zipCodeLabel")}
                      </label>
                      <input
                        id={`${uuid}-zip-mailing`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepThree-mailing-zipCodePlaceholder"
                        )}
                        {...FACStepThreeRegister("mailingZip", {
                          required: {
                            value: true,
                            message: t(
                              "AgentFACStepThree-mailing-zipCodeRequiredText"
                            ),
                          },
                          pattern: {
                            value: /^\d{5}$/,
                            message: t(
                              "AgentFACStepThree-mailing-zipCodePatternMessage"
                            ),
                          },
                          maxLength: {
                            value: 5,
                            message: t(
                              "AgentFACStepThree-mailing-zipCodemaxLengthMessage"
                            ),
                          },
                        })}
                      />
                      <FormInputError
                        id={`${uuid}-zip-mailing-error`}
                        errorMessage={errors.mailingZip?.message}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="md:w-4/5">
                <label htmlFor={`${uuid}-claim-number`} className="block mb-1">
                  {t("AgentFACStepThree-mailing-claimNumberLabel")}
                </label>
                <input
                  id={`${uuid}-claim-number`}
                  type="text"
                  placeholder={t(
                    "AgentFACStepThree-mailing-claimNumberPlaceholder"
                  )}
                  {...FACStepThreeRegister(`claimNumber`)}
                />
                <FormInputError
                  id={`${uuid}-claim-number-error`}
                  errorMessage={errors?.claimNumber?.message}
                />
              </div>

              <div>
                <label id="ID_messageLabel" htmlFor="ID_message">
                  {t("AgentFACStepThree-specialInstructionLabel", {
                    wordCount: MAXIMUMWORDCOUNT,
                  })}
                </label>
                <textarea
                  id="ID_message"
                  type="text"
                  rows="6"
                  placeholder={t(
                    "AgentFACStepThree-specialInstructionPlaceholder"
                  )}
                  {...FACStepThreeRegister("mailingComment", {
                    maxLength: {
                      value: MAXIMUMWORDCOUNT,
                      message: t(
                        "AgentFACStepThree-specialInstruction-maxLengthMessage"
                      ),
                    },
                    onChange: (e) => {
                      setCurrentWordCount(e.target.value.length);
                    },
                  })}
                />
                <div
                  className={
                    currentWordCount > MAXIMUMWORDCOUNT
                      ? "text-red-600 text-sm text-right"
                      : "text-sm text-right"
                  }
                >
                  {currentWordCount}/{MAXIMUMWORDCOUNT}
                </div>
                <FormInputError
                  id="ID_emailError"
                  errorMessage={errors?.mailingComment?.message}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                className="ui-btn-secondary ui-btn-min-w mt-4"
                type="button"
                onClick={() => setFormStep((current) => current - 1)}
              >
                {t("AgentFACStepThree-previousButtonLabel")}
              </button>

              <button className="ui-btn-primary ui-btn-min-w mt-4">
                {t("AgentFACStepThree-continueButtonLabel")}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
